import { Github, ExternalLink, ArrowUpRight } from 'lucide-react'

export default function ProjectCard({ project, index, onClick }) {
  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(project) }
  }

  // In-progress placeholder card
  if (project.inProgress) {
    return (
      <div
        className="relative flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#1e293b] bg-[#111111]/40 min-h-[260px]"
        style={{ transitionDelay: `${index * 80}ms` }}
      >
        <span className="font-mono text-3xl text-indigo-500/20 mb-4">{'{ }'}</span>
        <p className="font-mono text-sm text-indigo-400/60 mb-2">{project.title}</p>
        <p className="text-[#94a3b8] text-xs text-center max-w-xs px-6 leading-relaxed">
          {project.shortDescription}
        </p>
      </div>
    )
  }

  return (
    <div
      className="card-hover relative flex flex-col rounded-2xl border border-[#1e293b] bg-[#111111] overflow-hidden cursor-pointer group"
      style={{ transitionDelay: `${index * 80}ms` }}
      onClick={() => onClick(project)}
      onKeyDown={handleKey}
      role="button"
      tabIndex={0}
      aria-label={`View ${project.title} project details`}
    >
      {/* Hover top shine */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      {/* Card image area */}
      <div className="w-full h-36 bg-[#0d0d0d] border-b border-[#1e293b] flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-cyan-500/5" />
        <span className="font-mono text-5xl text-indigo-500/10 group-hover:text-indigo-500/20 transition-colors duration-300 select-none mr-1">{'{'}</span>
        <span className="font-mono text-5xl text-indigo-500/10 group-hover:text-indigo-500/20 transition-colors duration-300 select-none">{'}'}</span>
        {project.featured && (
          <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-xs font-mono font-semibold bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
            ★ Featured
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-5">
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3 className="text-lg font-bold text-[#f1f5f9] group-hover:text-indigo-300 transition-colors duration-200 leading-tight">
            {project.title}
          </h3>
          <ArrowUpRight
            size={17}
            className="text-[#94a3b8] group-hover:text-indigo-400 flex-shrink-0 mt-0.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200"
          />
        </div>

        <p className="text-[#94a3b8] text-sm leading-relaxed mb-4 flex-1">
          {project.shortDescription}
        </p>

        {/* Tech badges */}
        <div className="flex flex-wrap gap-1.5 mb-5">
          {project.tech.slice(0, 4).map(t => (
            <span key={t} className="px-2 py-0.5 rounded-full text-xs font-mono border border-[#1e293b] bg-[#0d0d0d] text-cyan-400/80">
              {t}
            </span>
          ))}
          {project.tech.length > 4 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-mono border border-[#1e293b] bg-[#0d0d0d] text-[#94a3b8]">
              +{project.tech.length - 4}
            </span>
          )}
        </div>

        {/* Links — stop propagation so card click doesn't also fire */}
        <div className="flex gap-2" onClick={e => e.stopPropagation()}>
          <a
            href={project.github}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${project.title} GitHub`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#1e293b] bg-[#0d0d0d] hover:border-indigo-500/50 text-[#94a3b8] hover:text-indigo-400 text-xs font-medium transition-all duration-200"
          >
            <Github size={12} /> Code
          </a>
          {project.demo && (
            <a
              href={project.demo}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${project.title} demo`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/30 hover:border-indigo-500/60 text-indigo-400 text-xs font-medium transition-all duration-200"
            >
              <ExternalLink size={12} /> Demo
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
